import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { Textarea } from '@/shared/components/ui/textarea';
import { toast } from 'sonner';
import {
  CreditCard, Plus, RefreshCw, Eye, MoreHorizontal,
  DollarSign, User, ArrowDownLeft, ArrowUpRight } from
'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from
'@/shared/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';

const API_URL = API_BASE;
const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('ezerpToken')}`
});

const statusColors = {
  draft: 'bg-gray-500/20 text-gray-400',
  applied: 'bg-green-500/20 text-green-400',
  partial: 'bg-amber-500/20 text-amber-400',
  unapplied: 'bg-blue-500/20 text-blue-400',
  voided: 'bg-red-500/20 text-red-400'
};

export const PaymentsPage = () => {const { t } = useTranslation("finance");
  const [activeTab, setActiveTab] = useState('receipts');
  const [receipts, setReceipts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateReceipt, setShowCreateReceipt] = useState(false);
  const [showCreatePayment, setShowCreatePayment] = useState(false);
  const [receiptForm, setReceiptForm] = useState({
    customer_id: '',
    receipt_date: new Date().toISOString().split('T')[0],
    amount: '',
    payment_method: 'bank_transfer',
    reference_no: '',
    notes: ''
  });
  const [paymentForm, setPaymentForm] = useState({
    vendor_id: '',
    payment_date: new Date().toISOString().split('T')[0],
    amount: '',
    payment_method: 'bank_transfer',
    reference_no: '',
    notes: ''
  });

  const fetchReceipts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/finance/ar/receipts`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setReceipts(Array.isArray(data) ? data : data.receipts || []);
      }
    } catch (err) {console.error(err);}
  };

  const fetchPayments = async () => {
    try {
      const res = await fetch(`${API_URL}/api/finance/ap/payments`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setPayments(Array.isArray(data) ? data : data.payments || []);
      }
    } catch (err) {console.error(err);}
  };

  const fetchCustomers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/finance/ar/customers`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setCustomers(Array.isArray(data) ? data : []);
      }
    } catch (err) {console.error(err);}
  };

  const fetchVendors = async () => {
    try {
      const res = await fetch(`${API_URL}/api/finance/ap/vendors`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setVendors(Array.isArray(data) ? data : []);
      }
    } catch (err) {console.error(err);}
  };

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([fetchReceipts(), fetchPayments(), fetchCustomers(), fetchVendors()]);
    setLoading(false);
  };

  useEffect(() => {fetchAll();}, []);

  const handleCreateReceipt = async () => {
    try {
      const payload = {
        ...receiptForm,
        customer_id: parseInt(receiptForm.customer_id),
        amount: parseFloat(receiptForm.amount)
      };
      const res = await fetch(`${API_URL}/api/finance/ar/receipts`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Failed to create receipt');
      toast.success('Receipt created');
      setShowCreateReceipt(false);
      setReceiptForm({
        customer_id: '', receipt_date: new Date().toISOString().split('T')[0],
        amount: '', payment_method: 'bank_transfer', reference_no: '', notes: ''
      });
      fetchReceipts();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleCreatePayment = async () => {
    try {
      const payload = {
        ...paymentForm,
        vendor_id: parseInt(paymentForm.vendor_id),
        amount: parseFloat(paymentForm.amount)
      };
      const res = await fetch(`${API_URL}/api/finance/ap/payments`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Failed to create payment');
      toast.success('Payment created');
      setShowCreatePayment(false);
      setPaymentForm({
        vendor_id: '', payment_date: new Date().toISOString().split('T')[0],
        amount: '', payment_method: 'bank_transfer', reference_no: '', notes: ''
      });
      fetchPayments();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const totalReceipts = receipts.reduce((sum, r) => sum + (r.amount || 0), 0);
  const totalPayments = payments.reduce((sum, p) => sum + (p.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("payments", "Payments")}</h1>
          <p className="text-muted-foreground mt-1">{t("manageCustomerReceipts", "Manage customer receipts and vendor payments")}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowCreateReceipt(true)} className="gap-2 bg-green-600 hover:bg-green-700">
            <ArrowDownLeft className="h-4 w-4" />{t("recordReceipt", "Record Receipt")}
          </Button>
          <Button onClick={() => setShowCreatePayment(true)} className="gap-2 bg-blue-600 hover:bg-blue-700">
            <ArrowUpRight className="h-4 w-4" />{t("makePayment", "Make Payment")}
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("totalReceipts", "Total Receipts")}</p>
                <p className="text-3xl font-bold text-green-500">${totalReceipts.toLocaleString()}</p>
              </div>
              <div className="p-3 rounded-lg bg-green-500/20"><ArrowDownLeft className="h-6 w-6 text-green-500" /></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("totalPayments", "Total Payments")}</p>
                <p className="text-3xl font-bold text-red-500">${totalPayments.toLocaleString()}</p>
              </div>
              <div className="p-3 rounded-lg bg-red-500/20"><ArrowUpRight className="h-6 w-6 text-red-500" /></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("receiptCount", "Receipt Count")}</p>
                <p className="text-3xl font-bold">{receipts.length}</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-500/20"><CreditCard className="h-6 w-6 text-blue-500" /></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("paymentCount", "Payment Count")}</p>
                <p className="text-3xl font-bold">{payments.length}</p>
              </div>
              <div className="p-3 rounded-lg bg-purple-500/20"><DollarSign className="h-6 w-6 text-purple-500" /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="pt-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="flex items-center justify-between mb-4">
              <TabsList>
                <TabsTrigger value="receipts" className="gap-2">
                  <ArrowDownLeft className="h-4 w-4" />{t("customerReceipts", "Customer Receipts")}
                </TabsTrigger>
                <TabsTrigger value="payments" className="gap-2">
                  <ArrowUpRight className="h-4 w-4" />{t("vendorPayments", "Vendor Payments")}
                </TabsTrigger>
              </TabsList>
              <Button variant="outline" onClick={fetchAll}><RefreshCw className="h-4 w-4" /></Button>
            </div>

            <TabsContent value="receipts">
              {loading ?
              <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500" />
                </div> :
              receipts.length === 0 ?
              <div className="text-center py-12">
                  <ArrowDownLeft className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                  <p className="text-lg text-muted-foreground">{t("noReceiptsFound", "No receipts found")}</p>
                </div> :

              <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-800/50">
                      <tr>
                        <th className="text-left p-3 font-medium">{t("receipt", "Receipt #")}</th>
                        <th className="text-left p-3 font-medium">{t("customer", "Customer")}</th>
                        <th className="text-left p-3 font-medium">{t("date", "Date")}</th>
                        <th className="text-left p-3 font-medium">{t("method", "Method")}</th>
                        <th className="text-left p-3 font-medium">{t("amount", "Amount")}</th>
                        <th className="text-left p-3 font-medium">{t("status", "Status")}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700">
                      {receipts.map((r) =>
                    <tr key={r.id} className="hover:bg-slate-800/30">
                          <td className="p-3 font-medium">{r.receipt_no || `#${r.id}`}</td>
                          <td className="p-3">{r.customer_name || `Customer #${r.customer_id}`}</td>
                          <td className="p-3">{r.receipt_date}</td>
                          <td className="p-3 capitalize">{r.payment_method?.replace('_', ' ')}</td>
                          <td className="p-3 font-medium text-green-400">${(r.amount || 0).toLocaleString()}</td>
                          <td className="p-3"><Badge className={statusColors[r.status]}>{r.status}</Badge></td>
                        </tr>
                    )}
                    </tbody>
                  </table>
                </div>
              }
            </TabsContent>

            <TabsContent value="payments">
              {loading ?
              <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500" />
                </div> :
              payments.length === 0 ?
              <div className="text-center py-12">
                  <ArrowUpRight className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                  <p className="text-lg text-muted-foreground">{t("noPaymentsFound", "No payments found")}</p>
                </div> :

              <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-800/50">
                      <tr>
                        <th className="text-left p-3 font-medium">{t("payment", "Payment #")}</th>
                        <th className="text-left p-3 font-medium">{t("vendor", "Vendor")}</th>
                        <th className="text-left p-3 font-medium">{t("date", "Date")}</th>
                        <th className="text-left p-3 font-medium">{t("method", "Method")}</th>
                        <th className="text-left p-3 font-medium">{t("amount", "Amount")}</th>
                        <th className="text-left p-3 font-medium">{t("status", "Status")}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700">
                      {payments.map((p) =>
                    <tr key={p.id} className="hover:bg-slate-800/30">
                          <td className="p-3 font-medium">{p.payment_no || `#${p.id}`}</td>
                          <td className="p-3">{p.vendor_name || `Vendor #${p.vendor_id}`}</td>
                          <td className="p-3">{p.payment_date}</td>
                          <td className="p-3 capitalize">{p.payment_method?.replace('_', ' ')}</td>
                          <td className="p-3 font-medium text-red-400">${(p.amount || 0).toLocaleString()}</td>
                          <td className="p-3"><Badge className={statusColors[p.status]}>{p.status}</Badge></td>
                        </tr>
                    )}
                    </tbody>
                  </table>
                </div>
              }
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Create Receipt Dialog */}
      <Dialog open={showCreateReceipt} onOpenChange={setShowCreateReceipt}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("recordCustomerReceipt", "Record Customer Receipt")}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>{t("customer", "Customer *")}</Label>
              <Select value={receiptForm.customer_id} onValueChange={(v) => setReceiptForm({ ...receiptForm, customer_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>
                  {customers.map((c) =>
                  <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("date", "Date *")}</Label>
                <Input type="date" value={receiptForm.receipt_date} onChange={(e) => setReceiptForm({ ...receiptForm, receipt_date: e.target.value })} />
              </div>
              <div>
                <Label>{t("amount", "Amount *")}</Label>
                <Input type="number" value={receiptForm.amount} onChange={(e) => setReceiptForm({ ...receiptForm, amount: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("paymentMethod", "Payment Method")}</Label>
                <Select value={receiptForm.payment_method} onValueChange={(v) => setReceiptForm({ ...receiptForm, payment_method: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">{t("bankTransfer", "Bank Transfer")}</SelectItem>
                    <SelectItem value="check">{t("check", "Check")}</SelectItem>
                    <SelectItem value="cash">{t("cash", "Cash")}</SelectItem>
                    <SelectItem value="credit_card">{t("creditCard", "Credit Card")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("reference", "Reference #")}</Label>
                <Input value={receiptForm.reference_no} onChange={(e) => setReceiptForm({ ...receiptForm, reference_no: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>{t("notes", "Notes")}</Label>
              <Textarea value={receiptForm.notes} onChange={(e) => setReceiptForm({ ...receiptForm, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateReceipt(false)}>{t("cancel", "Cancel")}</Button>
            <Button onClick={handleCreateReceipt} className="bg-green-600 hover:bg-green-700">{t("recordReceipt", "Record Receipt")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Payment Dialog */}
      <Dialog open={showCreatePayment} onOpenChange={setShowCreatePayment}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("makeVendorPayment", "Make Vendor Payment")}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>{t("vendor", "Vendor *")}</Label>
              <Select value={paymentForm.vendor_id} onValueChange={(v) => setPaymentForm({ ...paymentForm, vendor_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select vendor" /></SelectTrigger>
                <SelectContent>
                  {vendors.map((v) =>
                  <SelectItem key={v.id} value={String(v.id)}>{v.name}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("date", "Date *")}</Label>
                <Input type="date" value={paymentForm.payment_date} onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })} />
              </div>
              <div>
                <Label>{t("amount", "Amount *")}</Label>
                <Input type="number" value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("paymentMethod", "Payment Method")}</Label>
                <Select value={paymentForm.payment_method} onValueChange={(v) => setPaymentForm({ ...paymentForm, payment_method: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">{t("bankTransfer", "Bank Transfer")}</SelectItem>
                    <SelectItem value="check">{t("check", "Check")}</SelectItem>
                    <SelectItem value="cash">{t("cash", "Cash")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("reference", "Reference #")}</Label>
                <Input value={paymentForm.reference_no} onChange={(e) => setPaymentForm({ ...paymentForm, reference_no: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>{t("notes", "Notes")}</Label>
              <Textarea value={paymentForm.notes} onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreatePayment(false)}>{t("cancel", "Cancel")}</Button>
            <Button onClick={handleCreatePayment} className="bg-blue-600 hover:bg-blue-700">{t("makePayment", "Make Payment")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

};

export default PaymentsPage;